// playfulTitlePlaceholders — the whimsical "we're still writing" lines that
// cycle in the title slot of `StreamingStoryView` while the practice story
// body streams and no real title has arrived yet. Brand voice: short, playful,
// Australian whimsy (cf. the `<adjective> <australian-animal>` session naming).
//
// Two curated lists live here:
//
//   - PLAYFUL_TITLE_PLACEHOLDERS — the cycling "in progress" lines. Each ends
//     with an ellipsis so it reads as ongoing, never as a finished title.
//     Shared by both the online (SSE) and offline (WebLLM) paths via the
//     single StreamingStoryView title slot.
//
//   - PLAYFUL_FALLBACK_TITLES — *complete* playful titles (no trailing
//     ellipsis) used as the offline fallback when the post-story title
//     completion comes back empty / errors. Distinct from the placeholders so
//     a fallback never reads as "still loading".
//
// Both are plain data; the random-pick helpers below keep the "no immediate
// repeat" behaviour in one tested place. `Math.random()` is fine here — this
// is browser app code, not a workflow script.

/**
 * Whimsical placeholder lines shown (cycling) in the title slot while the
 * story body streams and no real title has landed yet. Each ends with "…" so
 * it reads as in-progress.
 */
export const PLAYFUL_TITLE_PLACEHOLDERS: readonly string[] = [
  'Dreaming up your story…',
  'Tossing in a few pickles…',
  'Brewing a ripper of a tale…',
  'Wrangling some wild words…',
  'Teaching a quokka to type…',
  'Polishing the opening line…',
  'Almost got a good one…',
  'Chasing the perfect plot…',
  'Sprinkling in a twist…',
  'Finding the funny bits…',
  'Warming up the vocal cords…',
  'Untangling a good yarn…',
  'Coaxing a koala off the keyboard…',
  'Stitching the scenes together…',
  'Letting the ideas bounce about…',
];

/**
 * Complete playful titles (no trailing ellipsis) used as the offline fallback
 * when the post-story title completion is empty or fails. NEVER the old single
 * static "A short piece for your voice" — always a fresh random pick.
 */
export const PLAYFUL_FALLBACK_TITLES: readonly string[] = [
  'A Tiny Tale, Just for You',
  'Words for a Wandering Voice',
  'Read Me Like You Mean It',
  'A Ripper Little Read',
  'Something to Say Out Loud',
  'A Short Piece Worth a Shout',
  'Your Stage, Your Story',
  'A Few Lines to Play With',
];

/**
 * Picks a random entry from `pool`, avoiding `previous` so the same line never
 * appears twice in a row. Returns the single element when the pool has one
 * (no choice to make), and ignores `previous` if it isn't in the pool.
 */
export function pickRandomDistinct(
  pool: readonly string[],
  previous?: string | null,
): string {
  if (pool.length === 0) return '';
  if (pool.length === 1) return pool[0];
  // Build the candidate set excluding `previous` so we never repeat. If
  // `previous` isn't in the pool the filter is a no-op.
  const candidates =
    previous == null ? pool : pool.filter((line) => line !== previous);
  const source = candidates.length > 0 ? candidates : pool;
  const index = Math.floor(Math.random() * source.length);
  return source[index];
}

/**
 * Random playful *placeholder* (the "…"-suffixed cycling lines), never the
 * same as `previous`.
 */
export function nextPlaceholder(previous?: string | null): string {
  return pickRandomDistinct(PLAYFUL_TITLE_PLACEHOLDERS, previous);
}

/**
 * Random *complete* playful title for the offline fallback path, never the
 * same as `previous`.
 */
export function randomFallbackTitle(previous?: string | null): string {
  return pickRandomDistinct(PLAYFUL_FALLBACK_TITLES, previous);
}
